import { MessageOutlined } from "@ant-design/icons"
import { envConfig } from "config/envConfig"
import { useFlag } from "modules/flags/flags"
import { useCallback, useMemo } from "react"
import { useUserRole } from "services/users"

declare global {
	interface Window {
		zE: (
			type: "messenger" | "messenger:on",
			command:
				| "loginUser"
				| "logoutUser"
				| "close"
				| "hide"
				| "open"
				| "show"
				| "unreadMessages",
			jwtCallback?: (callback: Function) => void,
			loginCallback?: (error: any) => void,
		) => void
	}
}

export function useZendesk() {
	const { data: { zendeskToken } = {} } = useUserRole()
	const { supportRequests } = useFlag("supportRequests")
	const isEnabled = !!envConfig.zendeskKey() && !supportRequests.enabled
	const jwtCallback = useCallback(
		(callback: Function) => {
			callback(zendeskToken)
		},
		[zendeskToken],
	)
	const open = useCallback(() => {
		if (typeof window.zE !== "function") {
			const script = document.createElement("script")

			script.id = "ze-snippet"
			script.src = `https://static.zdassets.com/ekr/snippet.js?key=${envConfig.zendeskKey()}`
			script.onload = () => {
				// Setup event handlers

				window.zE("messenger:on", "close", () => {
					// The widget shouldn't be visible after the user collapses it.
					// They will have to back to the Help menu again to re-open it.
					window.zE("messenger", "hide")
				})

				window.zE("messenger:on", "unreadMessages", () => {
					// Show the chat widget again if a new message is sent from the support team
					window.zE("messenger", "show")
				})

				// Initialize the chat widget immediately
				window.zE("messenger", "open")

				// Authenticate the user against Zendesk
				window.zE("messenger", "loginUser", jwtCallback, console.error)
			}

			document.body.appendChild(script)
		} else {
			window.zE("messenger", "show")
			window.zE("messenger", "open")
		}
	}, [jwtCallback])
	const logoutUser = useCallback(() => {
		if (typeof window.zE === "function") {
			window.zE("messenger", "logoutUser")
		}
	}, [])
	const items = useMemo(
		() =>
			isEnabled
				? [
						{
							key: "contact-support",
							label: "Contact support",
							icon: <MessageOutlined aria-hidden />,
							onClick: open,
						},
					]
				: [],
		[isEnabled, open],
	)

	return { items, open, logoutUser }
}
